import { Feather } from '@expo/vector-icons';
import React, { useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ModalProps,
  Platform,
  useWindowDimensions
} from 'react-native';
import styled from 'styled-components/native';
import IconButton from './IconButton';
import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import Colors from '../constants/Colors';
import { StudyParticipantImage } from '../models/StudyParticipantImage';
import { format } from 'date-fns';
import { B } from './Typography';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import { log } from '../tools/LogHelper';
import { stringify } from 'flatted';

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 52}px;
  left: 20px;
  z-index: 1;
`;

const StyledShareIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 48}px;
  right: 20px;
  z-index: 1;
`;

const StyledTitle = styled(B)`
  margin-top: ${Platform.OS === 'web' ? 16 : 42}px;
  text-align: center;
`;

const StyledToolbarView = styled.View<{ isDesktop: boolean }>`
  height: ${Platform.OS === 'web' ? 64 : 96}px;
  align-items: center;
  justify-content: center;
  ${p =>
    p.isDesktop
      ? ''
      : `position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  background-color: white;`}
`;

const StyledImage = styled.Image`
  width: 100%;
  height: 100%;
`;

const ImageModal: React.FC<
  ModalProps & {
    image?: StudyParticipantImage;
    imageUrl?: string;
  }
> = ({ image, imageUrl, ...props }) => {
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const { onRequestClose } = props;
  const [loading, setLoading] = useState(false);
  const onShare = async () => {
    setLoading(true);
    if (image?.originalUrl || imageUrl) {
      try {
        const destinationFile = new File(
          Paths.cache,
          encodeURIComponent(image?.filename || 'document')
        );
        const downloadedFile = await File.downloadFileAsync(
          image?.originalUrl || imageUrl || '',
          destinationFile,
          { idempotent: true }
        );
        await Sharing.shareAsync(downloadedFile.uri);
      } catch (error) {
        log('Sharing Error: ', stringify(error));
      }
    }

    setLoading(false);
  };
  return (
    <Modal animationType='slide' {...props}>
      <StyledToolbarView isDesktop={isDesktop}>
        <StyledTitle>
          {image?.created
            ? format(image.created, 'MMM d, yyyy h:mm a')
            : 'Image'}
        </StyledTitle>
        <StyledCloseIconButton onPress={onRequestClose}>
          <Feather name='arrow-left' size={28} color='black' />
        </StyledCloseIconButton>
        {Platform.OS !== 'web' && (
          <StyledShareIconButton onPress={!loading ? onShare : undefined}>
            {loading ? (
              <ActivityIndicator color={Colors.primaryPurple} />
            ) : (
              <Feather name='share' size={28} color='black' />
            )}
          </StyledShareIconButton>
        )}
      </StyledToolbarView>
      {Platform.OS !== 'web' ? (
        <ReactNativeZoomableView
          maxZoom={3}
          minZoom={0.5}
          zoomStep={1}
          initialZoom={1}
          bindToBorders={true}
        >
          <StyledImage
            source={{ uri: image?.largeUrl || imageUrl }}
            resizeMode='contain'
          />
        </ReactNativeZoomableView>
      ) : (
        <StyledImage
          source={{ uri: image?.largeUrl || imageUrl }}
          resizeMode='contain'
        />
      )}
    </Modal>
  );
};

export default ImageModal;
